import { useCallback, useContext } from 'react';

import {
  getUserSession,
  postUserUpdateInfo,
} from 'PrivateAPI';
import { getUserLogOut } from 'PrivateApis';

import { UserContext } from 'Context/user';

import { postBroadcast } from 'Utilities/broadcastChannel';

import { useFeatureFlags } from '../utilities/hooks';
import { useSetRefreshToken } from '../utilities/user/hooks/useSetRefreshToken';

type UpdateInfoPayload = {
  consent: { consent: boolean; marketing: boolean };
  email: string;
  id?: number;
  name: string;
  phone: string;
  postcode: string;
}

export const useUpdateInfoAction = () => {
  const { userActions: { update: updateUser }, userState } = useContext(UserContext);
  const { useAuthnIdToken } = useFeatureFlags() || {};
  const { setCognitoIdTokenRefreshInterval } = useSetRefreshToken();

  const isUseAuthnIdToken = useAuthnIdToken && !!userState?.cognitoTokens?.refreshToken;

  const updateUserInfoAction = useCallback(async (payload: Partial<UpdateInfoPayload>) => {
    const {
      changed,
      code,
      customer,
      error: apiError,
      message,
    } = await postUserUpdateInfo(payload)
      .catch((error) => ({ error }));

    const result = {
      changed,
      customer,
      error: apiError || code,
      message,
    };

    if (result.error) { // API Error
      window?.Sentry?.captureException?.(new Error(apiError));
      return result;
    }

    if (Number.isInteger(customer?.id)) {
      const { vehicles, ...seller } = customer;
      updateUser(seller);
    }

    const isAuthKeysChanged = ['auth_token', 'login_token', 'email'].some((val) => result.changed?.[val]);

    if (isAuthKeysChanged) {
      await getUserLogOut();
    }

    const sessionToken = isUseAuthnIdToken ? {
      refreshToken: userState?.cognitoTokens?.refreshToken,
    } : {
      token: customer.login_token,
    };

    const { error: sessionError } = await getUserSession(sessionToken)
      .catch((error) => ({ error: error?.message || error }));

    if (sessionError) {
      result.message = sessionError?.toString?.() || '';
      result.error = sessionError;

      window?.Sentry?.captureException?.(new Error(sessionError), {
        extra: {
          tag: 'update user info',
        },
        level: 'info',
      });
    } else {
      if (isAuthKeysChanged) {
        setCognitoIdTokenRefreshInterval();
      }
      postBroadcast();
    }

    return result;
  }, [isUseAuthnIdToken, setCognitoIdTokenRefreshInterval, updateUser, userState?.cognitoTokens?.refreshToken]);

  return {
    updateUserInfoAction,
  };
};
